<template>
  <b-input-group :size="size">
    <b-form-input
      :name="name"
      :id="id"
      :size="size"
      :state="computedState"
      v-model="maskValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      v-mask="mask"
      autocomplete="off"
    />
    <TimePicker
      v-if="time"
      :state="state"
      v-model="timePickerValue"
      v-bind="timePickerOptions"
      parseStrict
      />
    <b-input-group-append v-if="!(disabled || readonly)">
      <v-date-picker
      ref="picker"
      v-model="pickerValue"
      :min-date="notBefore"
      :max-date="notAfter"
      :locale="lang"
      :popover="calendarSettings"
      :attributes="calendarAttributes"
      :input-debunce="0"
      :rows="calendarRows"
      :columns="calendarColumns"
      @popoverWillShow="moveToDefault"
      :available-dates="availableDates"
      >
        <b-button :size="size" :variant="calendarButtonVariant"><b-icon icon="calendar" /></b-button>
      </v-date-picker>
    </b-input-group-append>
  </b-input-group>
</template>

<script>
import moment from 'moment'
import { getMaskForMomentFormat, adjustMask } from '../utils/mask'
import TimePicker from './timepicker'

export default {
  components: { TimePicker },
  data () {
    return {
      internalMaskValue: '',
      internalTimePickerValue: null
    }
  },
  props: {
    availableDates: {
      type: [Array, Object],
      default: () => { return null }
    },
    value: {
      type: [Date, String]
    },
    // by default don't return a date object, return a string
    valueAsDate: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: 'md'
    },
    disabled: Boolean,
    readonly: Boolean,
    // momentjs compatible format, only supports numerical formats (so no month names or week days)
    displayFormat: {
      type: String,
      default: 'DD/MM/YYYY'
    },
    // returned format (momentjs) - only used when valueAsDate is false
    returnFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    },
    state: {
      type: Boolean,
      default: null
    },
    defaultValue: {
      type: Date,
      required: false
    },
    notBefore: {
      type: Date,
      required: false
    },
    notAfter: {
      type: Date,
      require: false
    },
    name: {},
    id: {},
    placeholder: {
      type: String,
      default: 'dd/mm/yyyy'
    },
    calendarButtonVariant: {
      type: String,
      default: 'secondary'
    },
    calendarRows: {
      type: Number,
      default: 1
    },
    calendarColumns: {
      type: Number,
      default: 1
    },
    altCalendarHighlight: Object,
    time: {
      type: Boolean,
      default: false
    },
    timePickerOptions: {
      type: Object,
      default () { return {} }
    },
    calendarBoundryElement: String,
    todayLabel: {
      type: String,
      default: 'astazi'
    },
    selectedDateLabel: {
      type: String,
      default: 'data selectata'
    },
    lang: {
      type: String,
      default: 'ro'
    }
  },
  computed: {
    timeFormat () { return this.timePickerOptions.format || 'HH:mm' },
    mask () {
      return {
        guide: true,
        keepCharPositions: true,
        // TODO: improve this function massively
        mask: getMaskForMomentFormat(this.displayFormat)
      }
    },
    maskValue: {
      get () {
        return this.internalMaskValue
      },
      set (val) {
        this.internalMaskValue = val
        const mValue = moment(this.dateFromString(adjustMask(val)))
        this.setExternalValue(mValue)
      }
    },
    pickerValue: {
      get () {
        const mValue = moment(this.dateFromString(this.internalMaskValue))
        if (mValue.isValid()) return mValue.toDate()
        return null
      },
      set (val) {
        const format = this.displayFormat
        const value = moment(val)
        if (value.isValid()) {
          this.internalMaskValue = moment(val).format(format)
          this.setExternalValue(value)
        }
        // don't emit anything for invalid values, as they most likely come from outside the datepicker
      }
    },
    computedState () {
      const inp = this.internalMaskValue
      const format = this.displayFormat
      if (inp === '' || inp === null || moment(adjustMask(inp), format, true).isValid()) return this.state
      return false
    },
    calendarSettings () {
      return {
        modifiers: {
          preventOverflow: {
            enabled: true,
            boundariesElement: this.calendarBoundryElement ? document.getElementById(this.calendarBoundryElement) : 'window'
          }
        },
        placement: 'bottom-end',
        visibility: 'click'
      }
    },
    calendarAttributes () {
      const ret = [
        {
          key: 'today',
          bar: true,
          dates: new Date(),
          popover: {
            label: this.todayLabel
          }
        }
      ]
      if (this.pickerValue) {
        ret.push({
          key: 'selection',
          dates: this.pickerValue,
          popover: {
            label: this.selectedDateLabel
          }
        })
      }
      if (this.altCalendarHighlight) ret.push(this.altCalendarHighlight)
      return ret
    },
    timePickerValue: {
      get () {
        return this.internalTimePickerValue
      },
      set (val) {
        this.internalTimePickerValue = val
        // setter should only be called by timepicker component
        if (this.pickerValue) this.setExternalValue(moment(this.pickerValue))
      }
    }
  },
  methods: {
    moveToDefault () {
      if (this.pickerValue) return
      const calendar = this.$refs.picker.$refs.calendar
      if (this.defaultValue) calendar.move(this.defaultValue)
      else if (this.altCalendarHighlight && this.altCalendarHighlight.dates) {
        if (Array.isArray(this.altCalendarHighlight.dates)) calendar.move(this.altCalendarHighlight.dates[0])
        else calendar.move(this.altCalendarHighlight.dates)
      }
    },
    dateFromString (val) {
      // need to adjust string because mask directive sometimes leaves a placeholder char at the end
      const mDate = moment(adjustMask(val), this.displayFormat, true)
      if (mDate.isValid()) return mDate.toDate()
      return moment.invalid().toDate()
    },
    setExternalValue (momentValue) {
      let extValue
      let hasChanged = this.value
      if (momentValue.isValid() && (!this.time || this.timePickerValue)) {
        const mValue = moment(momentValue)
        if (this.time) {
          const tMoment = moment(this.timePickerValue, this.timeFormat, true)
          // set time
          mValue.hour(tMoment.hour())
          mValue.minute(tMoment.minute())
          mValue.second(tMoment.second())
        }
        const currentValue = this.valueAsDate ? moment(this.value) : moment(this.value, this.returnFormat, true)
        extValue = this.valueAsDate ? mValue.toDate() : mValue.format(this.returnFormat)
        hasChanged = !mValue.isSame(currentValue)
      } else extValue = null
      if (hasChanged) {
        this.$emit('input', extValue)
        this.$emit('change', extValue)
      }
    },
    initValues (val) {
      const maskMValue = moment(this.internalMaskValue, this.displayFormat, true)
      const mVal = this.valueAsDate ? moment(val) : moment(val, this.returnFormat, true)
      this.internalMaskValue = (mVal && mVal.isValid()) ? mVal.format(this.displayFormat) : (maskMValue.isValid() ? '' : this.internalMaskValue)
      if (this.time) {
        if (mVal && mVal.isValid()) this.internalTimePickerValue = mVal.format(this.timeFormat)
        else {
          this.internalTimePickerValue = this.internalTimePickerValue || this.timePickerOptions.defaultValue || null
        }
      }
    }
  },
  watch: {
    value (val) {
      this.initValues(val)
    }
  },
  mounted () {
    this.initValues(this.value)
  }
}
</script>
